import type { ReactNode } from "react";

import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import { GranularitySwitcherEnum } from "app/pages/industry-analysis/category-share/CategoryShareGraph/CategoryShareGraphTypes";
import DurationService from "app/services/DurationService";
import { entityTypeMap } from "pro-features/components/AutocompleteAdIntelligence/AutocompleteAdIntelligenceHelper";

import { PreviewPerformanceWidget } from "../../advertisers/overview/components/preview-performance-widget/PreviewPerformanceWidget";
import { PreviewPerformanceWidgetCompare } from "../../advertisers/overview/components/PreviewPerformanceWidgetCompare";
import { TopAdvertisersPublishersTable } from "../../common/advertisersPublishers/TopAdvertisersPublishers";
import { ActivityBreakdownCompare } from "../../common/overview/compare/ActivityBreakdownCompare";
import { PerformanceOverTimeCompareGraph } from "../../common/overview/compare/PerformanceOverTimeCompareGraph";
import { PerformanceWidgetCompare } from "../../common/overview/compare/PerformanceWidgetCompare";
import { PerformanceOverTimeGraph } from "../../common/overview/PerformanceOverTimeGraph";
import { PerformanceWidget } from "../../common/overview/PerformanceWidget";
import { RecentAdsGallery } from "../../common/overview/RecentAdsGallery";
import { BlurContainer } from "../../common/overview/StyledComponents";

import { TopIndustriesTable } from "./topIndustries/TopIndustriesTable";
const getSingleKeyPerformanceWidget = (graphComponent: ReactNode, isPreview = false) =>
    isPreview ? (
        <PreviewPerformanceWidget
            controller={entityTypeMap.publisher}
            performanceOverTimeGraphComponent={graphComponent}
        />
    ) : (
        <PerformanceWidget
            controller={entityTypeMap.publisher}
            performanceOverTimeGraphComponent={graphComponent}
        />
    );

const getMultiKeyPerformanceWidget = (isWindow: boolean, isOneMonthDuration: boolean, isPreview = false) =>
    isPreview ? (
        <>
            <PreviewPerformanceWidgetCompare controller={entityTypeMap.publisher} />
            <ActivityBreakdownCompare controller={entityTypeMap.publisher} />
            {!isWindow && !isOneMonthDuration ? (
                <PerformanceOverTimeCompareGraph
                    controller={entityTypeMap.publisher}
                    defaultGranularity={GranularitySwitcherEnum.MONTHLY}
                    hideGranularityFilter
                    isPreview
                />
            ) : null}
        </>
    ) : (
        <>
            <PerformanceWidgetCompare controller={entityTypeMap.publisher} />
            <ActivityBreakdownCompare controller={entityTypeMap.publisher} />
            {!isWindow && !isOneMonthDuration ? (
                <PerformanceOverTimeCompareGraph
                    controller={entityTypeMap.publisher}
                    defaultGranularity={GranularitySwitcherEnum.MONTHLY}
                    hideGranularityFilter
                />
            ) : null}
        </>
    );

export const PublisherAnalysisOverviewPage = ({ isPreview = false }: { isPreview?: boolean }) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const isWindow = !!DurationService.getDurationData(params.duration ?? "").forAPI.isWindow;
    const isOneMonthDuration = DurationService.isLessThanOrEqualToOneMonths(params.duration ?? "");
    const isCompareMode = (params.key?.split(",").length || 0) > 1;

    const GraphComponent =
        !isWindow && !isOneMonthDuration ? (
            <PerformanceOverTimeGraph
                isPreview={isPreview}
                controller={entityTypeMap.publisher}
                defaultGranularity={GranularitySwitcherEnum.MONTHLY}
                hideGranularityFilter
            />
        ) : null;

    const renderPreviewMode = () => (
        <>
            {isCompareMode
                ? getMultiKeyPerformanceWidget(isWindow, isOneMonthDuration, true)
                : getSingleKeyPerformanceWidget(GraphComponent, true)}
            <RecentAdsGallery
                controller={entityTypeMap.publisher}
                isPreview
            />
            <TopAdvertisersPublishersTable
                isPreview
                controller={entityTypeMap.publisher}
                resource={entityTypeMap.advertiser}
            />
        </>
    );

    const renderStandardMode = () => (
        <>
            {isCompareMode
                ? getMultiKeyPerformanceWidget(isWindow, isOneMonthDuration)
                : getSingleKeyPerformanceWidget(GraphComponent)}
            <TopAdvertisersPublishersTable
                controller={entityTypeMap.publisher}
                resource={entityTypeMap.advertiser}
            />
            <TopIndustriesTable />
            <RecentAdsGallery controller={entityTypeMap.publisher} />
        </>
    );

    return <BlurContainer $active={isPreview}>{isPreview ? renderPreviewMode() : renderStandardMode()}</BlurContainer>;
};
